import { useCallback } from 'react'
import { useParams } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { Eye, Shield } from 'lucide-react'
import { openMainPanelOverlayAtom } from '@/stores/mainPanelOverlayStore'
import {
  getToolCallId,
  isToolError,
  pickString,
  plural,
  readToolState,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = <Shield className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

function getControlFilterDetail(
  args: Record<string, unknown> | null | undefined
): string | null {
  return pickString(
    args?.frameworkControlRef,
    Array.isArray(args?.frameworkControlRefs)
      ? args.frameworkControlRefs
          .filter((ref) => typeof ref === 'string')
          .join(', ')
      : null,
    args?.controlId,
    args?.frameworkSlug,
    args?.applicability,
    args?.search
  )
}

export function ControlsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count = Array.isArray(state.result?.controls)
    ? state.result.controls.length
    : typeof state.result?.total === 'number'
      ? state.result.total
      : null

  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading controls...'
      completedLabel={
        count !== null ? `Found ${plural(count, 'control')}` : 'Controls loaded'
      }
      loadingDetail={getControlFilterDetail(state.args)}
      completedDetail={getControlFilterDetail(state.args)}
      completedIcon={completedIcon}
    />
  )
}

export function ControlsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const control = state.result?.control
  const params = useParams({ strict: false }) as { workspaceId?: string }
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const detail =
    pickString(control?.displayCode, control?.controlId, state.args?.controlId) ??
    pickString(state.args?.frameworkControlRef, state.args?.id)
  const controlCode = pickString(
    control?.displayCode,
    control?.controlId,
    state.args?.controlId
  )
  const controlTitle = pickString(control?.title) ?? 'Untitled control'
  const controlDefId = pickString(control?.id, state.args?.id)
  const canPreview =
    state.isComplete && !state.isError && !isToolError(state) && !!controlDefId
  const overlayId = getToolCallId(
    part,
    `control-${controlDefId ?? detail ?? 'detail'}`
  )

  const openControlPreview = useCallback(() => {
    if (!canPreview || !controlDefId || !params.workspaceId) return
    openOverlay({
      kind: 'system-detail',
      id: overlayId,
      source: 'chat',
      payload: {
        entityType: 'control',
        entityId: controlDefId,
        workspaceId: params.workspaceId,
      },
    })
  }, [canPreview, controlDefId, openOverlay, overlayId, params.workspaceId])

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading control...'
        completedLabel='Control loaded'
        loadingDetail={getControlFilterDetail(state.args)}
        completedDetail={detail}
        completedIcon={completedIcon}
      />
      {canPreview ? (
        <button
          type='button'
          onClick={openControlPreview}
          className='group mt-1 flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-divider hover:bg-surface-subtle'
        >
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-text-subtle'>
            <Shield className='h-4 w-4' />
          </span>
          <span className='flex min-w-0 flex-1 flex-col'>
            <span className='truncate text-[12px] font-medium text-text-secondary'>
              {controlCode ?? detail ?? 'Control'}
            </span>
            <span className='truncate text-[11px] text-text-subtle'>
              {controlTitle}
            </span>
          </span>
          <span className='inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-text-body transition-colors group-hover:bg-muted group-hover:text-text-secondary'>
            <Eye className='h-3.5 w-3.5' />
            Open
          </span>
        </button>
      ) : null}
    </>
  )
}
